/**
 * Per-brand AI-disclosure defaults, stored in `brand_profiles.ai_disclosure_defaults`
 * (jsonb, nullable). They seed the disclosure modal when a user exports/launches
 * an ad for that brand; the user can still override per export. A `null` column
 * means the brand hasn't configured defaults — readers fall back to
 * `DEFAULT_BRAND_DISCLAIMER_DEFAULTS`.
 *
 * Writes go through the brand-profile settings UI via Hasura's generated
 * mutations (this is a plain jsonb column with no backend write path), so the
 * stored value is not guaranteed well-formed. Always read it through
 * `parseBrandDisclaimerDefaults`, which validates and returns `null` on anything
 * malformed rather than trusting the shape.
 */

import {
  DEFAULT_DISCLAIMER_POSITION,
  DISCLAIMER_FONT_FAMILY,
  DISCLAIMER_FONT_SIZE,
  DISCLAIMER_POSITIONS,
  DISCLAIMER_TEXT,
} from "./constants";
import type { DisclaimerPosition, DisclaimerSpec } from "./types";

/**
 * How a disclosure type defaults for a brand:
 *   - "always": seed it on for every ad.
 *   - "whenAiGenerated": seed it on only when the ad contains AI-generated media.
 *   - "never": the brand has opted out — the disclosure is never seeded on.
 * "always" and "whenAiGenerated" keep AI ads disclosed and differ only for
 * non-AI ads; "never" disables the disclosure regardless of AI media.
 */
export const DISCLAIMER_APPLY_MODES = [
  "always",
  "whenAiGenerated",
  "never",
] as const;

export type DisclaimerApplyMode = (typeof DISCLAIMER_APPLY_MODES)[number];

/** Typography for the visible overlay. `family` is a CSS font-family value. */
export interface BrandDisclaimerFont {
  family: string;
  size: number;
}

export interface BrandDisclaimerDefaults {
  /** Default apply-mode for the visible "Contains AI-generated content" overlay. */
  visible: DisclaimerApplyMode;
  /** Default apply-mode for the machine-readable IPTC/XMP tag. */
  machineReadable: DisclaimerApplyMode;
  /** Default corner for the visible overlay. */
  position: DisclaimerPosition;
  /** Font for the visible overlay. */
  font: BrandDisclaimerFont;
}

/**
 * Defaults used to seed the settings form for a brand with no stored config,
 * and the fallback when the stored value is `null`/malformed. Mirrors what the
 * overlay renderer produces today (both disclosures on, system font).
 */
export const DEFAULT_BRAND_DISCLAIMER_DEFAULTS: BrandDisclaimerDefaults = {
  visible: "always",
  machineReadable: "always",
  position: DEFAULT_DISCLAIMER_POSITION,
  font: {
    family: DISCLAIMER_FONT_FAMILY,
    size: DISCLAIMER_FONT_SIZE,
  },
};

function isApplyMode(value: unknown): value is DisclaimerApplyMode {
  return DISCLAIMER_APPLY_MODES.includes(value as DisclaimerApplyMode);
}

function isPosition(value: unknown): value is DisclaimerPosition {
  return DISCLAIMER_POSITIONS.includes(value as DisclaimerPosition);
}

function isFont(value: unknown): value is BrandDisclaimerFont {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const font = value as Record<string, unknown>;
  return (
    typeof font.family === "string" &&
    typeof font.size === "number" &&
    Number.isFinite(font.size) &&
    font.size > 0
  );
}

/**
 * Validate an untrusted stored value into `BrandDisclaimerDefaults`. Returns
 * `null` if the value is absent or doesn't fully match the expected shape, so
 * callers fall back to `DEFAULT_BRAND_DISCLAIMER_DEFAULTS` rather than rendering
 * a partially-formed config.
 */
export function parseBrandDisclaimerDefaults(
  value: unknown,
): BrandDisclaimerDefaults | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  if (
    !isApplyMode(candidate.visible) ||
    !isApplyMode(candidate.machineReadable) ||
    !isPosition(candidate.position) ||
    !isFont(candidate.font)
  ) {
    return null;
  }
  return {
    visible: candidate.visible,
    machineReadable: candidate.machineReadable,
    position: candidate.position,
    font: { family: candidate.font.family, size: candidate.font.size },
  };
}

/**
 * Resolve a stored (untrusted) column value into a usable config, falling back
 * to `DEFAULT_BRAND_DISCLAIMER_DEFAULTS` when the value is absent or malformed.
 * The single entry point readers should use to turn
 * `brand_profiles.ai_disclosure_defaults` into a `BrandDisclaimerDefaults`.
 */
export function resolveBrandDisclaimerDefaults(
  value: unknown,
): BrandDisclaimerDefaults {
  return (
    parseBrandDisclaimerDefaults(value) ?? DEFAULT_BRAND_DISCLAIMER_DEFAULTS
  );
}

function appliesByMode(
  mode: DisclaimerApplyMode,
  hasAiGeneratedMedia: boolean,
): boolean {
  if (mode === "never") {
    return false;
  }
  return mode === "always" || hasAiGeneratedMedia;
}

/**
 * Whether a brand has opted out of AI disclosures entirely (both the visible
 * overlay and the machine-readable tag set to "never"). Callers use this to skip
 * the per-export disclosure dialog and download/launch directly with the
 * disabled spec — there is nothing for the user to configure.
 */
export function areBrandDisclaimerDefaultsDisabled(
  defaults: BrandDisclaimerDefaults,
): boolean {
  return defaults.visible === "never" && defaults.machineReadable === "never";
}

/**
 * Seed a `DisclaimerSpec` (the per-export modal selection) from a brand's
 * defaults. Each disclosure is enabled when its mode is `"always"`, or when it
 * is `"whenAiGenerated"` and the ad contains AI-generated media. The default
 * corner carries through.
 *
 * The overlay font is brand-fixed (not user-selectable), so it is intentionally
 * not part of `DisclaimerSpec` — the export path reads it from `defaults.font`
 * and passes it to the renderer separately.
 *
 * `hasAiGeneratedMedia` is supplied by the caller (derived at export time from
 * the ad's component-asset provenance), keeping this mapping pure.
 */
export function resolveDisclaimerSpecFromDefaults(
  defaults: BrandDisclaimerDefaults,
  { hasAiGeneratedMedia }: { hasAiGeneratedMedia: boolean },
): DisclaimerSpec {
  return {
    visible: appliesByMode(defaults.visible, hasAiGeneratedMedia),
    machineReadable: appliesByMode(
      defaults.machineReadable,
      hasAiGeneratedMedia,
    ),
    text: DISCLAIMER_TEXT,
    position: defaults.position,
  };
}
